import { useQuery } from "@tanstack/react-query";
import { UserPlus, UserRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { Modal } from "@/components/Modal";
import { MoneyText, useYen } from "@/components/MoneyText";
import { call } from "@/lib/api";
import type { CartCustomer, CustomerAttach, CustomerHit } from "@/lib/types";

/**
 * The second line of a search hit (T07-42, T01-20, design/04 §14): a cashier / PIN sees what
 * identity-service shows in its lookup view — **the name + the phone to its last 4 digits**
 * (`•••-••••-5678`), no e-mail; office roles see the phone and the e-mail in full. The masking is
 * identity-service's (pos-service passes it as it is) — never done here.
 */
export function customerLines(hit: CustomerHit): { title: string; detail: string } {
  const shown = [hit.phone ?? null, hit.limited ? null : hit.email].filter((part): part is string =>
    Boolean(part),
  );
  const title = hit.name ?? shown[0] ?? "";
  return { title, detail: shown.filter((part) => part !== title).join(" · ") };
}

function CustomerHitText({ hit }: { hit: CustomerHit }) {
  const { title, detail } = customerLines(hit);
  return (
    <span>
      <span className="block text-lg">{title}</span>
      {detail ? (
        <span
          data-testid="customer-detail"
          className="block text-sm text-muted-foreground tabular-nums"
        >
          {detail}
        </span>
      ) : null}
    </span>
  );
}

export interface CustomerModalProps {
  open: boolean;
  current: CartCustomer | null;
  onClose: () => void;
  onAttach: (body: CustomerAttach) => Promise<boolean>;
  onDetach: () => Promise<void>;
}

/**
 * 客 (design/05 §3.1): walk-in is the default of every receipt (R20 — nobody has to sign up);
 * find a customer by phone / name / e-mail (store credit balance shown; a cashier sees the name +
 * the masked phone — {@link customerLines}) or add a new one with name + phone / e-mail.
 */
export function CustomerModal({ open, current, onClose, onAttach, onDetach }: CustomerModalProps) {
  const { t } = useTranslation();
  const yen = useYen();
  const { api } = usePos();
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const term = q.trim();

  const found = useQuery({
    queryKey: ["pos-customers", "list", term],
    queryFn: () =>
      call(() => api.GET("/v1/pos/customers", { params: { query: { q: term, limit: 10 } } })),
    enabled: open && term.length >= 3,
    staleTime: 10_000,
  });

  const attach = async (body: CustomerAttach) => {
    setBusy(true);
    const ok = await onAttach(body);
    setBusy(false);
    if (ok) {
      setQ("");
      setCreating(false);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      title={t("customer.title")}
      description={t("customer.guest_note")}
      onClose={onClose}
      busy={busy}
      size="lg"
    >
      {current ? (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-md border bg-accent p-3">
          <span className="flex items-center gap-2 text-lg">
            <UserRound aria-hidden />
            {current.name ?? current.email ?? current.phone}
            {typeof current.storeCreditBalance === "number" ? (
              <span className="text-sm text-muted-foreground tabular-nums">
                {t("customer.credit_balance", { amount: yen(current.storeCreditBalance) })}
              </span>
            ) : null}
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void onDetach().finally(() => {
                setBusy(false);
                onClose();
              });
            }}
            className="min-h-pos-touch rounded-md border px-4"
          >
            {t("customer.detach")}
          </button>
        </div>
      ) : null}
      {creating ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim() || (!phone.trim() && !email.trim())) {
              return;
            }
            void attach({
              quick: {
                name: name.trim(),
                ...(phone.trim() ? { phone: phone.trim() } : {}),
                ...(email.trim() ? { email: email.trim() } : {}),
              },
            });
          }}
        >
          {(
            [
              ["name", name, setName, "text"],
              ["phone", phone, setPhone, "tel"],
              ["email", email, setEmail, "email"],
            ] as const
          ).map(([key, value, set, type]) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="font-medium">{t(`customer.${key}`)}</span>
              <input
                type={type}
                value={value}
                onChange={(e) => set(e.target.value)}
                className="min-h-pos-touch rounded-md border px-3 text-lg"
              />
            </label>
          ))}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="min-h-pos-touch rounded-md border px-6"
            >
              {t("app.back")}
            </button>
            <button
              type="submit"
              disabled={busy || !name.trim() || (!phone.trim() && !email.trim())}
              className="min-h-pos-touch rounded-md bg-primary px-6 font-semibold text-primary-foreground disabled:opacity-50"
            >
              {t("customer.create")}
            </button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-3">
          <input
            autoFocus
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("customer.search_placeholder")}
            aria-label={t("customer.search_placeholder")}
            className="min-h-pos-touch rounded-md border px-3 text-lg"
          />
          <ul className="flex max-h-80 flex-col gap-2 overflow-y-auto">
            {(found.data?.data ?? []).map((hit) => (
              <li key={hit.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void attach({ customerId: hit.id })}
                  className="flex min-h-pos-touch w-full items-center justify-between gap-3 rounded-md border px-4 py-2 text-left hover:bg-accent"
                >
                  <CustomerHitText hit={hit} />
                  {typeof hit.storeCreditBalance === "number" ? (
                    <MoneyText amount={hit.storeCreditBalance} size="sm" />
                  ) : null}
                </button>
              </li>
            ))}
            {term.length >= 3 && found.data && found.data.data.length === 0 ? (
              <li className="text-muted-foreground">{t("customer.none_found")}</li>
            ) : null}
          </ul>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border border-dashed text-lg"
          >
            <UserPlus aria-hidden />
            {t("customer.new")}
          </button>
        </div>
      )}
    </Modal>
  );
}
